<template>
  <div class="card overflow-hidden sleep-section-container">
    <div class="card-body pt-3 pb-4">
      <div class="row">
        <div class="col-12 d-flex justify-content-between">
          <div v-if="period" class="pt-2 text-lighter">
            <i class="fa fa-clock"></i
            ><span class="ms-2 font-weight-bold">{{
              formatPeriod(period)
            }}</span>
          </div>
          <div class="d-flex">
            <div class="form-group">
              <SectionDropdown
                id="ddSectionFilteSleepHours"
                :selected="selectedSection"
                @selected-section-id="
                  (selectedValue) => (selectedSection = selectedValue)
                "
              />
            </div>
          </div>
        </div>
        <div class="col-lg-8 col-12">
          <div class="text-xs text-uppercase text-primary text-bold mb-1">
            {{ header }}
          </div>
          <span class="h5 mt-2 text-uppercase">{{
            getSectionName(selectedSection)
          }}</span>
          <div v-if="!hasData" class="text-muted py-3">
            No data found for the selected date range.
          </div>
          <div v-show="hasData" class="my-4 chart">
            <canvas :id="id" class="chart-canvas"></canvas>
          </div>
        </div>

        <div v-show="hasData" class="col-lg-4 col-12">
          <div
            class="d-flex flex-column ms-3 mb-3 position-relative mt-sm-4 ms-sm-0 help"
          >
            <div class="text-bolder mb-2">What is Sleep Score?</div>
            <div class="mb-3 content">
              A sleep score is a numerical representation that aims to provide
              an overall assessment of the quality of your sleep based on
              various sleep-related metrics. These metrics include hours of
              sleep, sleep stages, wake events and more.
            </div>
            <div class="text-bolder mt-3 mb-2">
              What are the stages of sleep?
            </div>
            <div class="mb-3 content">
              When you sleep, your brain goes through natural cycles of
              activity. There are four stages of sleep - awake, light, REM and
              deep sleep. REM and deep sleep make up the restorative hours that
              allows the body and mind to recover, repair and rejuvenate.
            </div>

            <!-- table-->
            <div class="p-3 mt-3 mb-2 bg-help-table">
              <div class="content">
                <div class="text-uppercase text-bolder">
                  <div class="square me-2 bg-awake"></div>
                  Awake
                </div>
                <div class="text-break">
                  Even in bed, you may have periods of wakefulness.
                </div>
              </div>

              <div class="content mt-3">
                <div class="text-uppercase text-bolder">
                  <div class="square me-2 bg-sleep-light"></div>
                  Light
                </div>
                <div class="text-break">
                  The physiological stage of sleep that allows you to transition
                  to deep sleep.
                </div>
              </div>

              <div class="content mt-3">
                <div class="text-uppercase text-bolder">
                  <div class="square me-2 bg-sleep-rem"></div>
                  REM <span class="text-capitalize">(Rapid Eye Movement)</span>
                </div>
                <div class="text-break">
                  The mentally restorative sleep stage where your body
                  consolidates memory and learning.
                </div>
              </div>

              <div class="content mt-3">
                <div class="text-uppercase text-bolder">
                  <div class="square me-2 bg-sleep-deep"></div>
                  Deep
                </div>
                <div class="text-break">
                  The physically restorative sleep stage where your body
                  restores muscular, cellular, and skeletal systems.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const dayjs = require("dayjs");
let duration = require("dayjs/plugin/duration");
dayjs.extend(duration);

import Constants from "@/helpers/constants";
import Utils from "@/helpers/utils";
import Chart from "chart.js/auto";
import annotationPlugin from "chartjs-plugin-annotation";
import styles from "./SleepSectionChart.vue?vue&type=style&index=0&lang=scss&module=1";

import SectionDropdown from "@/views/pages/components/SectionDropdown.vue";

Chart.register(annotationPlugin);

function indexToMin(index) {
  return index - 0.36;
}

function indexToMax(index) {
  return index + 0.36;
}

function getTotalHours(ctx, index) {
  const chart = ctx.chart;
  let total = 0;

  for (let i = 0; i < chart.data.datasets.length; i++) {
    let dataset = chart.data.datasets[i];
    if (dataset.type == "bar") {
      total += dataset.data[index];
    }
  }
  return total;
}

// Limit lines for sleep scores
let annotations = [
  {
    id: "lineAverage",
    type: "line",
    borderColor: styles.colorLimitline,
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      position: "start",
    },
    scaleID: "y",
    value: 79,
  },
  {
    id: "lineLow",
    type: "line",
    borderColor: styles.colorLimitline,
    borderDash: [6, 6],
    borderDashOffset: 0,
    borderWidth: 1,
    label: {
      display: true,
      position: "start",
    },
    scaleID: "y",
    value: 49,
  },
];

// Label above bar
function addAnnotations(chart) {
  let max = chart.data.datasets[0].data.length;

  for (let idx = 0; idx < max; idx++) {
    annotations.push({
      id: idx.udid,
      type: "label",
      content: (ctx) => Utils.parseMinutesToHours(getTotalHours(ctx, idx)),
      color: styles.light,
      position: {
        x: "center",
        y: "end",
      },
      font: {
        family: "NotoSans-Regular",
      },
      xMax: indexToMax(idx) + 0.05,
      xMin: indexToMin(idx) - 0.05,
      xScaleID: "x",
      yMax: (ctx) => getTotalHours(ctx, idx),
      yMin: (ctx) => getTotalHours(ctx, idx),
      yScaleID: "y1",
    });
  }
  chart.update();
}

export default {
  name: "SleepSectionChart",
  components: {
    SectionDropdown,
  },
  props: {
    id: {
      type: String,
      default: "sleep-section-chart",
    },
    period: {
      type: Array,
      required: true,
    },
    header: {
      type: String,
      default: "Sleep Chart",
    },
    title: {
      type: String,
      default: "Today",
    },
    selectedSectionId: {
      type: Number,
      default: -1,
    },
    height: {
      type: [String, Number],
      default: "200",
    },
    dataSleepHours: {
      type: Object,
      required: true,
    },
    chart: {
      type: Object,
      required: true,
      labels: Array,
      datasets: {
        type: Array,
        label: String,
        data: Array,
        color: String,
      },
    },
  },
  emits: ["selected-section-id"],
  data() {
    return {
      selectedSection: Constants.DEFAULT_SECTION,
      annotationsAvg: [],
    };
  },
  computed: {
    hasData() {
      return this.dataSleepHours && this.dataSleepHours.length > 0;
    },
  },
  watch: {
    chart() {
      if (this.dataSleepHours && this.dataSleepHours.length > 0)
        this.addChart();
    },
    selectedSection() {
      this.$emit("selected-section-id", this.selectedSection);
    },
    selectedSectionId() {
      this.selectedSection = this.selectedSectionId;
    },
  },
  methods: {
    formatPeriod(period) {
      return Utils.formatPeriod(period);
    },
    getSectionName(section_id) {
      const section = this.$store.getters["list/getSectionName"](section_id);
      return section ? section.name : "";
    },
    addChart() {
      const ctx = document.getElementById(this.id).getContext("2d");

      let gradientStroke1 = ctx.createLinearGradient(0, 300, 0, 50);
      gradientStroke1.addColorStop(1, "rgba(59,121,130,0.15)");
      gradientStroke1.addColorStop(0.5, "transparent");
      gradientStroke1.addColorStop(0, "transparent");

      let chartStatus = Chart.getChart(this.id);
      if (chartStatus != undefined) {
        chartStatus.destroy();
      }

      let chart = new Chart(ctx, {
        data: {
          labels: this.chart.labels,
          datasets: [
            {
              type: "bar",
              label: this.chart.datasets[0].label,
              backgroundColor: this.chart.datasets[0].backgroundColor,
              data: this.chart.datasets[0].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[1].label,
              backgroundColor: this.chart.datasets[1].backgroundColor,
              data: this.chart.datasets[1].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[2].label,
              backgroundColor: this.chart.datasets[2].backgroundColor,
              data: this.chart.datasets[2].data,
              maxBarThickness: 30,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "bar",
              label: this.chart.datasets[3].label,
              backgroundColor: this.chart.datasets[3].backgroundColor,
              data: this.chart.datasets[3].data,
              maxBarThickness: 30,
              borderRadius: 4,
              order: 2,
              yAxisID: "y1",
            },
            {
              type: "line",
              label: this.chart.datasets[4].label,
              tension: 0.4,
              borderWidth: 0,
              pointRadius: 3,
              pointBackgroundColor: styles.colorSleep,
              borderColor: styles.colorSleep,
              // eslint-disable-next-line no-dupe-keys
              borderWidth: 3,
              backgroundColor: gradientStroke1,
              data: this.chart.datasets[4].data,
              fill: true,
              order: 1,
              yAxisID: "y",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              callbacks: {
                label: function (context) {
                  let label = context.dataset.label || "";
                  if (label && !label.startsWith("Average")) {
                    label += ": ";

                    if (
                      context.parsed.y !== null &&
                      typeof context.parsed.y === "number"
                    ) {
                      label += Utils.parseMinutesToHours(context.parsed.y);
                    }
                    return label;
                  } else return (label += ": " + context.parsed.y);
                },
              },
            },
            annotation: {
              annotations: annotations,
            },
            legend: {
              position: "bottom",
              labels: {
                color: styles.colorAxes,
                boxWidth: 12,
                boxHeight: 12,
                padding: 40,
              },
            },
          },
          interaction: {
            intersect: false,
            mode: "index",
          },
          scales: {
            y1: {
              stacked: true,
              display: false,
              position: "right",
              max: 24 * 60,
              ticks: {
                stepSize: 100,
                color: styles.gray,
              },
              title: {
                display: true,
                text: "Sleep Duration (Minutes)",
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: false, // only want the grid lines for one axis to show up
              },
            },
            x: {
              stacked: true,
              grid: {
                drawBorder: false,
                display: true,
                drawOnChartArea: true,
                drawTicks: true,
                borderDash: [5, 5],
              },
              ticks: {
                display: true,
                color: styles.gray,
                padding: 10,
                font: {
                  size: 12,
                  style: "normal",
                  lineHeight: 2,
                },
              },
            },
            y: {
              display: true,
              position: "left",
              min: 0,
              max: 100,
              ticks: {
                stepSize: 10,
                color: styles.gray,
              },
              title: {
                display: true,
                text: this.chart.datasets[4].label,
                color: styles.colorAxes,
                font: {
                  family: "NotoSans-Regular",
                },
              },
              // grid line settings
              grid: {
                drawOnChartArea: true,
              },
            },
          },
        },
      });

      addAnnotations(chart);
    },
  },
};
</script>

<style lang="scss" scoped>
 .sleep-section-container {
    padding-bottom: 0 !important;
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
} 
:export {
  gray: $color-muted-text;
  darkGray: $color-gridlines;
  light: #fff;
  colorSleep: $color-sleep;
  colorAxes: $color-chart-axes-text;
  colorLimitline: $light;
  colorSleepLight: $color-sleep-light;
  colorSleepRem: $color-sleep-rem;
  colorSleepDeep: $color-sleep-deep;
  colorAwake: $color-sleep-awake;
}
.square {
  height: 8px;
  width: 8px;
  border-radius: none;
  display: inline-block;
}

.bg-sleep-rem {
  background-color: $color-sleep-rem;
}
.bg-sleep-deep {
  background-color: $color-sleep-deep;
}
.bg-sleep-light {
  background-color: $color-sleep-light;
}
.bg-awake {
  background-color: $color-sleep-awake;
}
</style>
